import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { sharedApi, SHARED_ORIGIN } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AD } from "@/constants/testIds";
import { toast } from "sonner";
import { formatNZD, timeAgo, phoneDigits } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import Seo from "@/components/common/Seo";
import ImageLightbox from "@/components/common/ImageLightbox";
import SharePostCard from "@/components/detail/SharePostCard";
import FeedbackCard from "@/components/detail/FeedbackCard";
import ReviewCard, { StarSummary } from "@/components/detail/ReviewCard";
import AgentCard from "@/components/common/AgentCard";
import MoreBySellerStrip from "@/components/common/MoreBySellerStrip";
import ArrangeViewingCard from "@/components/detail/ArrangeViewingCard";
import NeighbourhoodCards from "@/components/detail/NeighbourhoodCards";
import { imgVariant } from "@/lib/tileSrc";
import {
  MapPin, Eye, Phone, MessageCircle, Loader2, ShieldCheck,
  Bed, Bath, Home, Sofa, CalendarClock, Hash,
  School, Compass, Navigation, Send, Mail, Maximize2, ImageOff, Link2,
} from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1560184897-ae75f418493e?auto=format&fit=crop&w=1200&q=60";
const CLASSIFIEDS_BASE = SHARED_ORIGIN;

function priceLabel(listing) {
  if (listing.price_type === "negotiable" || listing.price_type === "contact") return "Price Subject to Negotiation";
  if (!listing.price || Number(listing.price) <= 0) return "Price Subject to Negotiation";
  return formatNZD(listing.price);
}

function splitDot(v) {
  return String(v || "").split(/\s+·\s+/).filter(Boolean);
}

/**
 * Property variant of the ad detail page. Legacy render order.
 */
export default function PropertyAdView({ listing, seo }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [tab, setTab] = useState("location");

  // Reset gallery/lightbox/revealed state when SPA-navigating between
  // property detail pages so a high index or open lightbox doesn't leak
  // across listings.
  useEffect(() => {
    setActiveImg(0);
    setLightboxOpen(false);
    setRevealed(null);
  }, [listing.id]);

  const copyListingLink = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied!");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const hasPhotos = Array.isArray(listing.images) && listing.images.length > 0;
  const images = hasPhotos ? listing.images : [];
  const attrs = listing.attributes || {};
  const amenities = splitDot(attrs.Amenities);
  const openHomes = splitDot(attrs["Open Home Times"]).map((row) => {
    const [from, to] = row.split(/\s*→\s*/);
    return { from: (from || "").trim(), to: (to || "").trim() };
  });
  const address = attrs.ad_address || attrs.Address || [listing.area, listing.city, listing.region, "New Zealand"].filter(Boolean).join(", ");
  const bedrooms = attrs.Bedrooms;
  const bathrooms = attrs.Bathrooms;
  const furnishing = attrs.Furnishing;
  const secondaryContact = attrs["Secondary Contact"];

  // Hide non-render attribute keys from the Details grid — these are consumed
  // by the AgentCard, secondary-contact block, address/map, amenities grid, and
  // Open Home Times list. Leaking them as raw text chips (e.g. a raw Cloudinary
  // URL for "Agency Logo") is a bug.
  const HIDDEN_ATTR_KEYS = new Set([
    "Agency Name", "Agency Logo", "Agency License",
    "Secondary Contact", "Secondary Contact Photo",
    "Secondary Contact Phone", "Secondary Contact WhatsApp", "Secondary Contact Email",
    "Address", "ad_address", "Open Home Times", "Amenities",
  ]);
  // Iter-267 §1: defensive belt-and-braces guard — even though the
  // backend now strips vehicle mirror-form fields from non-vehicle
  // categories at create/edit/read time, PropertyAdView is a
  // specialised renderer that never touches DetailsRow. If a legacy
  // payload ever leaked Make/Model/Year (or any vehicle-only key),
  // this filter keeps them out of the Property detail grid.
  const VEHICLE_ONLY_ATTR_KEYS = new Set([
    "Make", "Model", "Model Detail",
    "Year", "End Year",
    "Odometer", "Kilometres", "Mileage",
    "Transmission", "Body Style", "Fuel Type", "Engine Size",
    "Doors", "Seats", "Drive Type",
    "Registration", "WOF Expiry", "Rego Expiry",
  ]);
  const detailEntries = Object.entries(attrs).filter(
    ([k]) => !HIDDEN_ATTR_KEYS.has(k) && !VEHICLE_ONLY_ATTR_KEYS.has(k),
  );

  const revealMut = useMutation({
    mutationFn: async () => (await sharedApi.post(`/classifieds/listings/${listing.id}/contact`)).data,
    onSuccess: (data) => setRevealed(data),
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not reveal contact"),
  });

  const schoolsQ = useQuery({
    queryKey: ["nearby-schools", listing.id],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/listings/${listing.id}/nearby-schools`);
      return data.schools || [];
    },
    enabled: !!(listing.lat && listing.lng),
    retry: 0,
  });

  // Iter 739 — one call feeds the map (precise re-geocoded pin), the
  // Schools tab (nearby w/ distances + markers) and the "About this
  // neighbourhood" cards. 404 = ad has no coordinates at all.
  const hoodQ = useQuery({
    queryKey: ["neighbourhood", listing.id],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/listings/${listing.id}/neighbourhood`);
      return data;
    },
    retry: 0,
    staleTime: 10 * 60_000,
  });
  const geo = hoodQ.data || null;

  const handleReveal = () => {
    if (!user) return nav(`/login?next=${encodeURIComponent(window.location.pathname)}`);
    revealMut.mutate();
  };

  // Iter 739 — prefer the neighbourhood endpoint's coordinates: when
  // precision === "address" the backend re-geocoded the street address
  // (many older ads were pinned to the city centroid only).
  const pinLat = geo?.lat ?? listing.lat;
  const pinLng = geo?.lng ?? listing.lng;

  const mapImg = geo?.map_url
    ? `${CLASSIFIEDS_BASE}${geo.map_url}`
    : pinLat && pinLng
    ? `${CLASSIFIEDS_BASE}/api/geo/places/static-map?lat=${pinLat}&lng=${pinLng}&zoom=15&width=640&height=300`
    : null;

  // Schools tab — same map with one marker per nearby school.
  const nearbySchools = geo?.nearby?.length
    ? geo.nearby
    : (schoolsQ.data || []).map((s) => ({ ...s, distance_km: s.distance_km }));
  const schoolMarkers = nearbySchools
    .filter((s) => s.lat && s.lng)
    .map((s) => `${s.lat},${s.lng}`)
    .join("|");
  const schoolMapImg = mapImg
    ? schoolMarkers
      ? `${mapImg}&markers=${encodeURIComponent(schoolMarkers)}`
      : mapImg
    : null;

  const directionUrl = pinLat && pinLng
    ? `https://www.google.com/maps/dir/?api=1&destination=${pinLat},${pinLng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  const mapsSearchUrl = pinLat && pinLng
    ? `https://www.google.com/maps/search/?api=1&query=${pinLat},${pinLng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  // Iter 739 Block B is property-only (this view already is), but guard on
  // the category anyway in case the renderer is ever reused.
  const isPropertyCat = /propert|real-estate|^rent$|flatmate/.test(
    `${listing.parent_category_slug || ""} ${listing.category_slug || ""}`,
  );

  const seoOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const seoCanonical = `${seoOrigin}${typeof window !== "undefined" ? window.location.pathname : ""}`;
  const seoPrice = Number(listing.price);
  const fallbackLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: seoOrigin || "/" },
        {
          "@type": "ListItem",
          position: 2,
          name: listing.category_name || listing.category_slug,
          item: `${seoOrigin}/c/${listing.category_slug}`,
        },
        { "@type": "ListItem", position: 3, name: listing.title, item: seoCanonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: listing.title,
      description: listing.description || "",
      image: images.slice(0, 4),
      url: seoCanonical,
      category: listing.category_name || listing.category_slug,
      ...(seoPrice > 0 && {
        offers: {
          "@type": "Offer",
          price: seoPrice,
          priceCurrency: "NZD",
          availability: "https://schema.org/InStock",
          url: seoCanonical,
          areaServed: listing.city || listing.region || "New Zealand",
          seller: {
            "@type": listing.seller_verified ? "Organization" : "Person",
            name: listing.seller_name || "Allsale seller",
          },
        },
      }),
    },
  ];
  // WO 800 §5 — RealEstateListing (or Product) payload from the SEO
  // endpoint wins; client-built blocks are only the fallback.
  const jsonLd = seo?.json_ld?.length ? seo.json_ld : fallbackLd;

  return (
    <div data-testid={AD.root} className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <Seo
        title={seo?.title || listing.title}
        description={seo?.description || `${listing.title} — ${priceLabel(listing)}. ${(listing.description || "").slice(0, 140)}`}
        image={images[0]}
        url={seo?.canonical}
        type="product"
        jsonLd={jsonLd}
        robots={seo?.robots && seo.robots !== "index,follow" ? seo.robots : undefined}
      />
      <Breadcrumbs
        items={[
          // Iter-216: prepend the parent category (e.g. "Property") when the
          // backend attaches it — so we render "Property › Houses & Apartments
          // › <title>" for subcategory listings. Legacy payloads without
          // parent_category_name keep the two-level breadcrumb.
          ...(listing.parent_category_id && listing.parent_category_name
            ? [{
                label: listing.parent_category_name,
                to: `/c/${listing.parent_category_slug || listing.parent_category_id}`,
              }]
            : []),
          { label: listing.category_name || "Property", to: `/c/${listing.category_slug}` },
          { label: listing.title },
        ]}
      />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT column */}
        <div className="lg:col-span-8 space-y-6">
          {/* Gallery */}
          <div data-testid={AD.gallery} className="rounded-2xl overflow-hidden bg-white shadow-card">
            <div className="aspect-[16/10] bg-slate-100 relative">
              {hasPhotos ? (
                <>
                  <img
                    src={imgVariant(images[activeImg], 1600)}
                    alt={listing.title}
                    className="w-full h-full object-cover cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                    onError={(e) => (e.currentTarget.src = FALLBACK)}
                  />
                  {listing.is_featured && <span className="ribbon">Featured</span>}
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    data-testid="ad-gallery-expand"
                    aria-label="View photos full-screen"
                    className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 transition-colors"
                  >
                    <Maximize2 className="w-3.5 h-3.5" /> {images.length} photo{images.length === 1 ? "" : "s"}
                  </button>
                </>
              ) : (
                <div
                  data-testid="ad-gallery-empty"
                  className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400"
                >
                  <ImageOff className="w-10 h-10" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">No photos</span>
                </div>
              )}
            </div>
            {hasPhotos && images.length > 1 && (
              <div className="p-3 flex gap-2 overflow-x-auto no-scrollbar">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => { setActiveImg(i); if (i === activeImg) setLightboxOpen(true); }}
                    onDoubleClick={() => { setActiveImg(i); setLightboxOpen(true); }}
                    data-testid={`ad-thumb-${i}`}
                    className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-[color:var(--brand-blue)]" : "border-transparent"} transition-colors`}
                  >
                    <img src={imgVariant(src, 320, 70)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Title + property chips */}
          <div className="rounded-2xl bg-white shadow-card p-6 md:p-8">
            <h1 data-testid={AD.title} className="font-display text-2xl md:text-4xl font-black text-slate-900 leading-tight tracking-tight">{listing.title}</h1>
            {(listing.review_count ?? 0) > 0 && (
              <div className="mt-3">
                <StarSummary avg={listing.review_avg} count={listing.review_count} />
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {bedrooms && <Chip icon={<Bed className="w-3.5 h-3.5" />} label={`${bedrooms} Bed${bedrooms === "1" ? "" : "s"}`} />}
              {bathrooms && <Chip icon={<Bath className="w-3.5 h-3.5" />} label={`${bathrooms} Bath${bathrooms === "1" ? "" : "s"}`} />}
              {furnishing && <Chip icon={<Sofa className="w-3.5 h-3.5" />} label={furnishing} />}
              {attrs.Type && <Chip icon={<Home className="w-3.5 h-3.5" />} label={attrs.Type} />}
            </div>
          </div>

          {/* Price card with address + listing_no + date + views */}
          <div className="rounded-2xl bg-white shadow-card p-6 md:p-8">
            <div data-testid={AD.price} className="font-display text-3xl md:text-4xl font-black text-[color:var(--brand-blue)]">
              {priceLabel(listing)}
            </div>
            <div className="mt-3 text-slate-700 text-sm font-medium">{address}</div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600 pt-3 border-t border-subtle">
              {listing.listing_no != null && (
                <span className="inline-flex items-center gap-1"><Hash className="w-3.5 h-3.5" /> Listing No : #{listing.listing_no}</span>
              )}
              <span>{new Date(listing.created_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {listing.views ?? 0} views</span>
            </div>
          </div>

          {/* Amenities */}
          {amenities.length > 0 && (
            <Section title="Amenities">
              <div className="flex flex-wrap gap-2">
                {amenities.map((a, i) => (
                  <span key={i} className="inline-flex items-center rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3 py-1.5 text-xs font-semibold">{a}</span>
                ))}
              </div>
            </Section>
          )}

          {/* Details pills — Iter-382 spec (label dark, value brand orange) */}
          {detailEntries.length > 0 && (
            <Section title="Details">
              <div className="flex flex-wrap gap-2" data-testid="property-details-pills">
                {detailEntries.map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-flex items-baseline gap-1.5 rounded-full bg-white border border-slate-200 shadow-sm px-3 py-1.5 text-sm max-w-full"
                  >
                    <span className="font-semibold text-slate-700 whitespace-nowrap">{k}</span>
                    <span className="font-bold text-[color:var(--brand-orange)] break-words">{String(v)}</span>
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Open Home Times */}
          {openHomes.length > 0 && (
            <Section title="Open Home Times" icon={<CalendarClock className="w-4 h-4" />}>
              <ul className="divide-y divide-subtle">
                {openHomes.map((row, i) => (
                  <li key={i} className="py-2 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">From</div>
                      <div className="mt-0.5 text-slate-900 font-semibold">{row.from}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">To</div>
                      <div className="mt-0.5 text-slate-900 font-semibold">{row.to}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Description */}
          {listing.description && (
            <Section title="Description">
              <p data-testid={AD.description} className="text-slate-800 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
            </Section>
          )}

          {/* Arrange a Viewing — shared widget (Iter-259). */}
          <Section title="Arrange a Viewing">
            <ArrangeViewingCard listingId={listing.id} hideHeading />
          </Section>

          {/* Map tabs */}
          <Section title="Location, Schools & Directions">
            <div className="flex items-center gap-2 border-b border-subtle mb-4">
              {[
                { k: "location", l: "Location", icon: <MapPin className="w-3.5 h-3.5" /> },
                ...(nearbySchools.length > 0 ? [{ k: "school", l: "Schools", icon: <School className="w-3.5 h-3.5" /> }] : []),
                { k: "direction", l: "Direction", icon: <Navigation className="w-3.5 h-3.5" /> },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`map-tab-${t.k}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                    tab === t.k
                      ? "border-[color:var(--brand-blue)] text-[color:var(--brand-blue)]"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t.icon} {t.l}
                </button>
              ))}
            </div>
            {tab === "location" && (
              <div className="rounded-xl overflow-hidden bg-slate-100">
                {mapImg ? (
                  <>
                    <a href={mapsSearchUrl} target="_blank" rel="noreferrer">
                      <img src={mapImg} alt="Location map" className="w-full h-auto" data-testid="map-location-img" />
                    </a>
                    <p className="px-4 py-2.5 bg-white border-t border-subtle text-sm text-slate-600">
                      <MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-[color:var(--brand-blue)]" />
                      <a href={mapsSearchUrl} target="_blank" rel="noreferrer" className="hover:underline">
                        {address}
                      </a>
                    </p>
                  </>
                ) : (
                  <div className="p-8 text-center text-slate-500 text-sm">
                    <Compass className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <a href={directionUrl} target="_blank" rel="noreferrer" className="font-semibold text-[color:var(--brand-blue)] hover:underline">Open in Google Maps</a>
                  </div>
                )}
              </div>
            )}
            {tab === "school" && (
              <div className="space-y-3">
                {schoolMapImg && <img src={schoolMapImg} alt="Nearby schools" className="w-full rounded-xl" data-testid="map-schools-img" />}
                <ul className="grid md:grid-cols-2 gap-2">
                  {nearbySchools.map((s, i) => (
                    <li key={i} className="rounded-xl border border-soft bg-white p-3 text-sm">
                      <div className="font-semibold text-slate-800 flex items-center gap-1.5">
                        <School className="w-3.5 h-3.5 text-[color:var(--brand-blue)] shrink-0" /> {s.name}
                        {s.distance_km != null && (
                          <span className="font-normal text-xs text-slate-400">· {s.distance_km} km</span>
                        )}
                      </div>
                      {s.address && <div className="text-xs text-slate-500 mt-0.5">{s.address}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {tab === "direction" && (
              <a href={directionUrl} target="_blank" rel="noreferrer" data-testid="map-direction-link" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5 transition-colors">
                <Navigation className="w-4 h-4" /> Get directions in Google Maps
              </a>
            )}
          </Section>

          {/* Iter 739 — Block B: About this neighbourhood (school zones +
              commute), fed by the same neighbourhood call. */}
          {isPropertyCat && geo && <NeighbourhoodCards geo={geo} />}

          {/* More listings by this seller — horizontal strip (up to 7) */}
          <MoreBySellerStrip
            sellerId={listing.seller_id}
            sellerName={listing.seller_name}
            currentAdId={listing.id}
          />

          {/* Iter-237 — public review card at the end of the main column. Share &
              Feedback live in the right sidebar for parity with legacy layout. */}
          <ReviewCard
            listingId={listing.id}
            ownerId={listing.seller_id || listing.user_id}
            avg={listing.review_avg}
            count={listing.review_count}
          />
        </div>

        {/* RIGHT sidebar — legacy parity: 5 stacked cards */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-4">
            {/* 1. Price + address */}
            <div className="rounded-2xl bg-white shadow-card p-6" data-testid="sidebar-price-card">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Asking price</div>
              <div className="mt-1 font-display text-3xl font-black text-[color:var(--brand-blue)]">{priceLabel(listing)}</div>
              <div className="mt-3 text-sm text-slate-600">{address}</div>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600 pt-3 border-t border-subtle">
                {listing.listing_no != null && (
                  <span data-testid="sidebar-listing-no" className="inline-flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" /> Listing No : #{listing.listing_no}
                    <button
                      type="button"
                      onClick={copyListingLink}
                      data-testid="sidebar-copy-link-btn"
                      aria-label="Copy listing link"
                      title="Copy listing link"
                      className="ml-0.5 p-1 rounded text-slate-400 hover:text-[color:var(--brand-blue)] hover:bg-slate-100 transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                <span data-testid="sidebar-date-posted">{new Date(listing.created_at).toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" })}</span>
                <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {listing.views ?? 0}</span>
              </div>
            </div>

            {/* 2. Seller / Agent — premium agency card when agency attrs are present, otherwise the standard seller card */}
            {(attrs["Agency Name"] || attrs["Agency Logo"]) ? (
              <AgentCard
                listing={listing}
                attrs={attrs}
                revealed={revealed}
                revealMut={revealMut}
                onReveal={handleReveal}
              />
            ) : (
            <div className="rounded-2xl bg-white shadow-card p-6" data-testid="sidebar-seller-card">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Connect with seller</div>
              <div className="mt-3 flex items-center gap-3">
                {listing.seller_photo ? (
                  <img src={listing.seller_photo} alt={listing.seller_name} data-testid="seller-photo" className="w-14 h-14 rounded-full object-cover border border-soft" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] font-bold grid place-items-center text-lg">
                    {(listing.seller_name || "S").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-semibold text-slate-800">{listing.seller_name}</div>
                  {listing.seller_since && (
                    <div className="text-xs text-slate-500" data-testid="seller-since">Member since {new Date(listing.seller_since).toLocaleDateString("en-NZ", { month: "long", year: "numeric" })}</div>
                  )}
                </div>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                  {revealed?.seller_email ? (
                    <a href={`mailto:${revealed.seller_email}?subject=${encodeURIComponent(listing.title)}`} className="font-semibold text-[color:var(--brand-blue)] hover:underline clamp-1">{revealed.seller_email}</a>
                  ) : (
                    <span className="text-slate-400">Sign in to view</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                  {revealed?.phone ? (
                    <a href={`tel:${phoneDigits(revealed.phone)}`} data-testid={AD.phoneNumber} className="font-semibold text-[color:var(--brand-blue)] hover:underline">{revealed.phone}</a>
                  ) : (
                    <span className="text-slate-400">Sign in to view</span>
                  )}
                </div>
              </div>

              <div className="mt-5 space-y-2">
                <button
                  data-testid={AD.revealPhoneBtn}
                  onClick={handleReveal}
                  disabled={revealMut.isPending || !!revealed}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-bold text-white bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-70 transition-colors"
                >
                  {revealMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  {/* Iter-375 — explicit sign-in copy for anonymous visitors */}
                  {revealed ? "Contact revealed" : user ? "Contact with seller" : "Sign in to view contact details"}
                </button>
                {revealed?.whatsapp && (
                  <a data-testid={AD.whatsappLink} href={`https://wa.me/${phoneDigits(revealed.whatsapp)}?text=${encodeURIComponent(`Kia Ora — I'm interested in "${listing.title}"`)}`} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-bold text-white transition-colors" style={{ backgroundColor: "var(--whatsapp)" }}>
                    <MessageCircle className="w-4 h-4" /> WhatsApp seller
                  </a>
                )}
                {listing.seller_id && (
                  <Link
                    to={`/seller/${encodeURIComponent(listing.seller_id)}`}
                    data-testid="sidebar-view-more-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 font-bold text-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] hover:bg-white border border-[color:var(--brand-blue)] transition-colors text-sm"
                  >
                    View more listings by {listing.seller_name}
                  </Link>
                )}
              </div>

              {(revealed?.secondary_contact || revealed?.secondary_contact_phone || secondaryContact) && (
                <div className="mt-5 pt-4 border-t border-subtle" data-testid="ad-more-contact-info">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">More contact info</div>
                  {revealed?.secondary_contact && <div className="text-sm text-slate-700 mb-1">{revealed.secondary_contact}</div>}
                  {revealed?.secondary_contact_phone && (
                    <a href={`tel:${phoneDigits(revealed.secondary_contact_phone)}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline">
                      <Phone className="w-3.5 h-3.5" /> {revealed.secondary_contact_phone}
                    </a>
                  )}
                  {!revealed && secondaryContact && <div className="text-xs text-slate-500">Sign in and reveal for full contact details.</div>}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-subtle flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                Meet in a public place. Never send money before viewing.
              </div>
            </div>
            )}

            {/* 3. Share Post — Iter-237 canonical (WhatsApp/FB/Telegram/X) */}
            <div data-testid="sidebar-share-card">
              <SharePostCard
                url={typeof window !== "undefined" ? window.location.href : ""}
                title={listing.title}
              />
            </div>

            {/* 4. Provide Feedback — Iter-237 per-listing lead modal */}
            <div data-testid="sidebar-feedback-card">
              <FeedbackCard listingId={listing.id} />
            </div>

            {/* 5. Chat box */}
            {listing.allow_chat && (
              <div className="rounded-2xl bg-white shadow-card overflow-hidden" data-testid="sidebar-chat-card">
                <div className="p-4 border-b border-subtle flex items-center gap-2">
                  {listing.seller_photo ? (
                    <img src={listing.seller_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] font-bold grid place-items-center text-sm">
                      {(listing.seller_name || "S").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 text-sm clamp-1">{listing.seller_name}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest">Chat with seller</div>
                  </div>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const msg = e.currentTarget.elements.msg.value.trim();
                    if (!msg) return;
                    if (!user) return nav(`/login?next=${encodeURIComponent(window.location.pathname)}`);
                    sharedApi.post("/classifieds/chats", { listing_id: listing.id, message: msg })
                      .then((res) => {
                        const cid = res?.data?.id || res?.data?.chat_id;
                        toast.success("Message sent");
                        e.target.reset();
                        if (cid) nav(`/chat/${cid}`);
                      })
                      .catch((err) => toast.error(err?.response?.data?.detail || "Could not send"));
                  }}
                  className="p-3 flex items-center gap-2"
                  data-testid="sidebar-chat-form"
                >
                  <input
                    name="msg"
                    placeholder="Type your message here…"
                    className="flex-1 rounded-full bg-slate-50 border border-transparent focus:border-[color:var(--brand-blue)] px-4 py-2 text-sm focus:outline-none"
                    data-testid="sidebar-chat-input"
                  />
                  <button type="submit" data-testid="sidebar-chat-send" className="rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white w-9 h-9 grid place-items-center transition-colors">
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {lightboxOpen && (
        <ImageLightbox
          images={images}
          index={activeImg}
          onIndexChange={setActiveImg}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

function Chip({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3.5 py-1.5 text-xs font-bold ring-1 ring-[color:var(--brand-blue)]/15">
      {icon} <span className="text-slate-900">{label}</span>
    </span>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl bg-white shadow-card p-6 md:p-8">
      <h2 className="font-display text-lg font-bold text-slate-900 mb-4 inline-flex items-center gap-2">{icon} {title}</h2>
      {children}
    </section>
  );
}
